// The keyboard router: the impure edge between key presses and commands.
//
// The pipeline (docs/design.md, "Keyboard"):
//
//   key press -> parser.step (pure) -> Command -> a scope's update (pure) -> effects
//
// The router reads a key event into the parser's input (the chord, what is
// live, whether a text field is typed in), sends it to the keyboard
// Program (keys/program.ts), and offers the commands that come back to the
// scopes. It owns what needs the page: the typing predicate, IME
// composition, Esc leaving a text field, open dialogs holding the keys,
// and knowing which embedded frame has the focus. It reads no key itself:
// which key names which command is the bindings table's alone.
//
// Views, popovers, dialogs and text fields register scopes. A scope names
// the sections of the table that are live while it is, and a `handle`
// that answers commands. Scopes rank by kind, so the order components
// happen to mount in never decides which one wins; within a kind, the
// scope registered last is innermost:
//
//   field (the text field the key is typed in)
//   modal (a dialog: nothing under it sees a key)
//   popover
//   view
//   page
//
// A command goes to the innermost scope first and outward while each
// passes it on. A command no scope answers is said to have nothing to act
// on here, so no command is dropped in silence.

import type { Command } from '../commands/command';
import type { Outcome } from '../commands/effects';
import { phrase } from '../commands/words';
import { Program } from '../runtime/effects';
import { DEFAULT_BINDINGS, keysFor, type Bindings, type ScopeKind, type Section } from './bindings';
import { chordOf } from './chord';
import type { ControlKeymap } from './control';
import type { Context, Match, Pending } from './parser';
import { initKeys, updateKeys, type KeysModel, type KeysMsg, type Notice, type Offer } from './program';
import { focusInFrame, isTextEntry, leavesField } from './typing';

export type { ScopeKind };

const RANK: Record<ScopeKind, number> = { page: 0, view: 1, popover: 2, modal: 3, field: 4 };

/**
 * Answers a command: `handled` (or nothing) when the scope carried it
 * out or said why it could not, `pass` when it is not this scope's (the
 * next scope out is asked), `declined` (or false) to leave the key to the
 * browser or the focused control.
 */
export type Handle = (command: Command) => Outcome | boolean | void;

/** What the key guide says over a page: set by the page's scope. */
export interface GuideSpec {
  heading: string;
  /** Said under the heading; `backticks` mark keys. */
  intro: string;
  /** Sections of the page's other views, listed with the live ones. */
  related: readonly Section[];
  /** Keys focused controls handle themselves, listed after the table's. */
  controls: readonly ControlKeymap<unknown>[];
  /** Whether the page has anything `command` could act on; commands it has not are left out. Every command by default. */
  offers?: (command: Command) => boolean;
}

export interface ScopeSpec {
  kind: ScopeKind;
  /** The sections of the bindings table live while the scope is registered, the one that wins a shared key first. */
  sections: readonly Section[];
  handle: Handle;
  /** Where the focus goes when Esc leaves a text field under this scope (the innermost scope's home wins). */
  home?: () => HTMLElement | null | undefined;
  /** For a `field` scope: the element whose keys it takes. */
  within?: Element;
  guide?: GuideSpec;
}

/** A registered scope: calling it removes the scope; `update` swaps its spec, keeping its place among scopes of its kind. */
export interface Registration {
  (): void;
  update(spec: ScopeSpec): void;
}

interface Scope {
  spec: ScopeSpec;
  order: number;
}

/** Controls that use Space and Enter themselves: a row marked `yields` leaves those keys to them. */
const USES_KEYS = 'a[href],button,input,select,summary,[role=button],[role=tab]';

export interface RouterOptions {
  now?: () => number;
  /** Whether a dialog is open; one no scope registered holds every key. */
  modalOpen?: () => boolean;
  /** Called when the scopes, the focused frame or the keyboard's state change. */
  onChange?: () => void;
  bindings?: Bindings;
}

export class Router {
  #scopes: Scope[] = [];
  #order = 0;
  #now: () => number;
  #modalOpen: () => boolean;
  #onChange: () => void;
  #program: Program<KeysModel, KeysMsg, Offer>;
  /** The scopes the key being dispatched meets, and how they answered what it named. */
  #meeting: Scope[] = [];
  // The port's answer comes back through these fields: `send` is synchronous and a key is never dispatched from inside another's.
  #answer: { offer: Offer; outcome: Outcome | 'unanswered' | null; ran: Match | null } | null = null;
  /** The deprecated aliases already said to be going away (once each, while the page lives). */
  #told = new Set<string>();
  /** The embedded frame that has the focus: its keys never reach this document, so the shortcuts are paused. */
  frame: Element | null = null;

  constructor(o: RouterOptions = {}) {
    this.#now = o.now ?? (() => Date.now());
    this.#modalOpen = o.modalOpen ?? (() => typeof document !== 'undefined' && document.querySelector('dialog[open]') !== null);
    this.#onChange = o.onChange ?? (() => {});
    this.#program = new Program<KeysModel, KeysMsg, Offer>({
      init: () => initKeys(o.bindings ?? DEFAULT_BINDINGS),
      update: updateKeys,
      port: (offer) => {
        const answered = offer.matches.length ? this.#offer(this.#meeting, offer.matches) : null;
        this.#answer = { offer, outcome: answered?.outcome ?? null, ran: answered?.ran ?? null };
      },
      now: this.#now,
    });
    // A store tells its state at once on subscribing: only what comes after is a change.
    let told = false;
    this.#program.subscribe(() => {
      if (told) this.#onChange();
      told = true;
    });
  }

  /** The table in use. */
  get bindings(): Bindings {
    return this.#program.state.bindings;
  }

  /** Use `bindings` from now on (the default table with an overlay, say). The caller checks it first (`validate`). */
  setBindings(bindings: Bindings): void {
    this.#program.send({ kind: 'bindings', bindings });
  }

  /** What is typed so far and what may follow, while the parser waits for a key. */
  get pending(): Pending | null {
    return this.#program.state.pending;
  }

  /** What was last said about a key, while it shows. */
  get notice(): Notice | null {
    return this.#program.state.notice;
  }

  /** Say `text` to the person, small and for a short while. */
  notify(text: string): void {
    this.#program.send({ kind: 'notice', text });
  }

  /** Time passing, as the parser reads it (for tests: the Program's own timers send this). */
  elapse(at: number): void {
    this.#program.send({ kind: 'elapsed', at });
  }

  /** Registers a scope; see `Registration`. */
  push(spec: ScopeSpec): Registration {
    const scope: Scope = { spec, order: this.#order++ };
    this.#scopes.push(scope);
    this.#onChange();
    const off = () => {
      const i = this.#scopes.indexOf(scope);
      if (i < 0) return;
      this.#scopes.splice(i, 1);
      this.#onChange();
    };
    return Object.assign(off, {
      update: (next: ScopeSpec) => {
        scope.spec = next;
        this.#onChange();
      },
    });
  }

  #sorted(): Scope[] {
    return [...this.#scopes].sort((a, b) => RANK[b.spec.kind] - RANK[a.spec.kind] || b.order - a.order);
  }

  /** The scopes a key on `target` meets, innermost first: the field scopes holding it, then the rest down to (and including) the first modal. */
  #stack(target: Element | null): Scope[] {
    const out: Scope[] = [];
    for (const s of this.#sorted()) {
      if (s.spec.kind === 'field') {
        if (target && s.spec.within?.contains(target)) out.push(s);
        continue;
      }
      out.push(s);
      if (s.spec.kind === 'modal') break;
    }
    return out;
  }

  /** The scopes the key guide describes, innermost first: views, popovers and pages (not dialogs or text fields). */
  active(): readonly ScopeSpec[] {
    return this.#sorted()
      .filter((s) => s.spec.kind !== 'field' && s.spec.kind !== 'modal')
      .map((s) => s.spec);
  }

  /** The sections live for a key on `target` (the page by default), innermost first. */
  live(target: Element | null = null): Section[] {
    return this.#stack(target).flatMap((s) => s.spec.sections);
  }

  /**
   * Offers each command to the scopes, innermost first, until one answers
   * it: the first command a scope carries out is the one that ran. A scope
   * that declines ends that command's turn; the next command a key named
   * (an outer section's row) is then tried.
   */
  #offer(scopes: readonly Scope[], matches: readonly Match[]): { outcome: Outcome | 'unanswered'; ran: Match | null } {
    for (const m of matches) {
      let answered = false;
      for (const s of scopes) {
        const r = s.spec.handle(m.command);
        if (r === 'pass') continue;
        if (r === false || r === 'declined') {
          answered = true;
          break;
        }
        return { outcome: 'handled', ran: m };
      }
      if (!answered) {
        this.notApplicable(`${phrase(m.command)}: nothing to do that to here`);
        return { outcome: 'unanswered', ran: null };
      }
    }
    return { outcome: 'declined', ran: null };
  }

  /**
   * A deprecated alias that ran says, the first time, what replaces it and
   * where the keys are listed: unless the command it ran said something
   * itself, which is not talked over (the alias keeps its say for next time).
   */
  #deprecated(m: Match, said: boolean) {
    if (!m.deprecated || said || this.#told.has(m.deprecated)) return;
    this.#told.add(m.deprecated);
    const shortest = (keys: string[]) => keys.reduce<string | null>((best, k) => (best === null || k.length < best.length ? k : best), null);
    const now = keysFor(this.bindings, m.command, this.live())[0] ?? keysFor(this.bindings, m.command)[0];
    const guide = shortest(keysFor(this.bindings, { kind: 'open', menu: 'guide' }));
    this.notify(`${m.deprecated} ${now ? `is now ${now}` : 'is going away'}${guide ? ` — see ${guide}` : ''}`);
  }

  /** Carries `command` out as a key would: offered to the scopes the page has now. For a button that does what a key does. */
  run(command: Command): void {
    const scopes = this.#stack(typeof document !== 'undefined' ? document.activeElement : null);
    this.#offer(scopes, [{ section: 'app', command }]);
  }

  /** Say that a command has nothing to act on here, and the keys of the command that may have been meant. */
  notApplicable(why: string, instead?: Command): void {
    const keys = instead ? keysFor(this.bindings, instead, this.live()) : [];
    this.notify(instead && keys.length ? `${why} · ${phrase(instead)}: ${keys[0]}` : why);
  }

  /** The keys that reach `command` on the page as it is, as written for a person; empty when none do. */
  keysFor(command: Command): string[] {
    return keysFor(this.bindings, command, this.live());
  }

  /**
   * Routes one keydown; true when the parser or a scope took it, and its
   * default action is suppressed. Modifier keys alone, Alt combinations
   * and every key of an IME composition never reach the parser (see
   * `chordOf`).
   */
  dispatch(e: KeyboardEvent): boolean {
    // An inner control (a tab list, a menu) already used the key.
    if (e.defaultPrevented) return false;
    const chord = chordOf(e);
    if (!chord) return false;
    const target = e.target instanceof Element ? e.target : null;
    const scopes = this.#stack(target);
    if (this.#modalOpen() && !scopes.some((s) => s.spec.kind === 'modal')) return false;
    const typing = isTextEntry(target);
    const context: Context = {
      sections: scopes.flatMap((s) => s.spec.sections.map((section) => ({ section, field: s.spec.kind === 'field' }))),
      typing,
      onControl: !!target?.closest(USES_KEYS),
    };
    this.#meeting = scopes;
    this.#answer = null;
    const notices = this.#program.state.notices;
    this.#program.send({ kind: 'key', chord, at: this.#now(), context });
    const answer = this.#answer as { offer: Offer; outcome: Outcome | 'unanswered' | null; ran: Match | null } | null;
    this.#meeting = [];
    if (answer?.ran) this.#deprecated(answer.ran, this.#program.state.notices !== notices);
    if (answer?.offer.taken && answer.outcome !== 'declined') {
      e.preventDefault();
      return true;
    }
    // Esc leaves a text field, so the keys work again; the focus goes to the innermost scope's home.
    if (typing && leavesField(e)) {
      (target as HTMLElement | null)?.blur?.();
      scopes.find((s) => s.spec.home?.())?.spec.home?.()?.focus({ preventScroll: true });
      e.preventDefault();
      return true;
    }
    return false;
  }

  #setFrame(doc: Document) {
    const frame = focusInFrame(doc) ? doc.activeElement : null;
    if (frame === this.frame) return;
    this.frame = frame;
    this.#onChange();
  }

  /** Listens to `win`'s keys, and to its focus moving into and out of embedded frames; the function returned stops. */
  attach(win: Window): () => void {
    const doc = win.document;
    let recheck: ReturnType<typeof setTimeout> | undefined;
    const onKey = (e: KeyboardEvent) => void this.dispatch(e);
    // A press anywhere is the person turning to something else: nothing typed stays pending.
    const onPointer = () => this.#program.send({ kind: 'reset' });
    // The window loses the focus as a frame takes it; the frame is the active element by then, or just after.
    const onBlur = () => {
      this.#program.send({ kind: 'reset' });
      this.#setFrame(doc);
      clearTimeout(recheck);
      recheck = setTimeout(() => this.#setFrame(doc), 0);
    };
    const onFocus = () => {
      clearTimeout(recheck);
      if (this.frame === null) return;
      this.frame = null;
      this.#onChange();
    };
    win.addEventListener('keydown', onKey);
    win.addEventListener('pointerdown', onPointer, true);
    win.addEventListener('blur', onBlur);
    win.addEventListener('focus', onFocus);
    return () => {
      clearTimeout(recheck);
      win.removeEventListener('keydown', onKey);
      win.removeEventListener('pointerdown', onPointer, true);
      win.removeEventListener('blur', onBlur);
      win.removeEventListener('focus', onFocus);
    };
  }
}
